// Layer 3 (Transformation): display splits for the hardness pair (polish.P11).
// Pure string work over `hardnessPair`'s figures — no new numbers.
import type { hardnessPair } from './Scene.transform';

type Meter = ReturnType<typeof hardnessPair>['feed']['meter'];

/** "71.7 stph" → figure "71.7", unit "stph"; a value with no unit stays whole. */
export function valueParts(value: string): { figure: string; unit: string } {
  const match = /^(\S+)\s+([A-Za-zµ/%][\w/%µ]*)$/.exec(value);
  return match ? { figure: match[1]!, unit: match[2]! } : { figure: value, unit: '' };
}

/** The meter's scale under the track: its zero, the normal band and its end. */
export function meterScale(meter: Meter, unit: string): { start: string; band: string; end: string } {
  const whole = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return {
    start: '0',
    band: `normal ${whole(meter.band.low)}–${whole(meter.band.high)}`,
    end: `${whole(meter.max)}${unit ? ` ${unit}` : ''}`,
  };
}

/** The feed card's sub-line with its design comparison removed: the meter's
 *  design tick already carries it, and the Crushing cell owns
 *  "71.7 stph · 75.0 design" (polish.P11 r1, say it once). What remains —
 *  the confirmed phase's "cut 10% recommended" — reads as its own sentence. */
export function feedSubLine(sub: string): string | null {
  const rest = sub.replace(/^vs [\d.,]+ \S+ design(?: · )?/, '').trim();
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : null;
}

/** The JT-023 sparkline's height, the one the card renders it at. */
export const KW_TREND_HEIGHT = 64;

/** The JT-023 sparkline's y-domain — one fixed domain in every phase, anchored
 *  to the series' control band rather than to its own range, so the design-basis
 *  noise draws flat and the hardness step climbs the card at the comp's rate.
 *  The domain runs from the LCL to one band-width above the UCL, so the
 *  confirmed rise covers about three quarters of the 40 px plot, as the comp
 *  draws it (komatsu.F11 / #737 — it drew about a third while the domain ran a
 *  band below the LCL to two above the UCL). The domain widens further only if a
 *  point falls outside it, so the line is never clipped. */
export function kwTrendDomain(
  points: readonly number[],
  limits: { lcl: number; ucl: number } | undefined,
): [number, number] | undefined {
  if (!limits) return undefined;
  const finite = points.filter((value) => Number.isFinite(value));
  const band = limits.ucl - limits.lcl;
  const lo = Math.min(limits.lcl, ...finite);
  const hi = Math.max(limits.ucl + band, ...finite);
  return [lo, hi];
}
